import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  Clock,
  Copy,
  Link2,
  Loader2,
  Mail,
  Plug,
  Plus,
  RadioTower,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  Settings as SettingsIcon,
  ShieldCheck,
  Sparkles,
  Tag,
  Unplug,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { absTime, relTime } from "@/lib/format";
import { colLabel, groupedColumns } from "@/lib/columns";
import { UsersSection } from "@/components/settings/UsersSection";
import {
  errMessage,
  getSettings,
  gmailDisconnect,
  gmailOauthStart,
  gmailPubsubEvents,
  gmailStartWatch,
  gmailStopWatch,
  gmailSync,
  putSettings,
  reclassifyAll,
  resetCsvFormat,
} from "@/lib/api";

const SAVE_KEYS = [
  "csv_format",
  "csv_filename_prefix",
  "approver_emails",
  "required_fields",
  "gmail_query",
  "sync_window_days",
  "polling_enabled",
  "polling_interval_minutes",
  "max_threads_per_sync",
  "auto_classify",
  "auto_extract_approvals",
  "pubsub_enabled",
  "pubsub_topic",
  "auto_label",
  "label_name",
];

const pick = (obj) =>
  SAVE_KEYS.reduce((acc, k) => {
    acc[k] = obj?.[k];
    return acc;
  }, {});

const SectionCard = ({ icon: Icon, title, description, action, children, testId, className }) => (
  <Card
    className={cn(
      "border-border bg-card shadow-[0_1px_0_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.06)]",
      className
    )}
    data-testid={testId}
  >
    <CardHeader className="flex flex-col gap-3 pb-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary">
          <Icon className="h-4 w-4" aria-hidden="true" />
        </span>
        <div>
          <CardTitle className="text-[15px] font-semibold tracking-tight">{title}</CardTitle>
          {description ? (
            <CardDescription className="mt-1 text-xs leading-relaxed">{description}</CardDescription>
          ) : null}
        </div>
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </CardHeader>
    <CardContent className="space-y-4 pt-0">{children}</CardContent>
  </Card>
);

const ToggleRow = ({ id, label, hint, checked, onCheckedChange, testId, disabled }) => (
  <div className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-3">
    <div className="min-w-0">
      <Label htmlFor={id} className="cursor-pointer text-[13px] font-medium">
        {label}
      </Label>
      {hint ? <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
    <Switch
      id={id}
      checked={!!checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      data-testid={testId}
    />
  </div>
);

const NumberRow = ({ id, label, hint, value, onChange, min = 1, max = 3650, suffix, fallback, testId }) => (
  <div className="rounded-lg border border-border bg-card p-3">
    <Label htmlFor={id} className="text-[13px] font-medium">
      {label}
    </Label>
    <div className="mt-2 flex items-center gap-2">
      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        onBlur={(e) => {
          const n = Number(e.target.value);
          if (e.target.value === "" || Number.isNaN(n)) onChange(fallback ?? min);
          else onChange(Math.min(max, Math.max(min, n)));
        }}
        className="h-9 w-28 font-mono [font-variant-numeric:tabular-nums]"
        data-testid={testId}
      />
      {suffix ? <span className="text-xs text-muted-foreground">{suffix}</span> : null}
    </div>
    {hint ? <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p> : null}
  </div>
);

export default function SettingsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [approverInput, setApproverInput] = useState("");
  const [fieldQuery, setFieldQuery] = useState("");
  const [purgeMail, setPurgeMail] = useState(false);
  const [events, setEvents] = useState([]);
  const oauthHandled = useRef(false);

  const load = useCallback(async () => {
    try {
      const data = await getSettings();
      setPayload(data);
      setDraft(pick(data.settings));
    } catch (e) {
      toast.error("Could not load settings", { description: errMessage(e) });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // OAuth redirect feedback (?gmail=connected | ?gmail=error&reason=...)
  useEffect(() => {
    if (oauthHandled.current) return;
    const params = new URLSearchParams(location.search);
    const state = params.get("gmail");
    if (!state) return;
    oauthHandled.current = true;
    if (state === "connected") {
      toast.success("Gmail connected", { description: "The first sync is running in the background." });
      setTimeout(load, 2500);
    } else {
      toast.error("Gmail connection failed", { description: params.get("reason") || "Please try again." });
    }
    navigate("/settings", { replace: true });
  }, [location.search, navigate, load]);

  const gmail = payload?.gmail || {};
  const account = gmail.account || {};
  const settings = payload?.settings || {};

  const dirty = useMemo(
    () => !!draft && !!payload && JSON.stringify(draft) !== JSON.stringify(pick(settings)),
    [draft, payload, settings]
  );

  const set = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        ...draft,
        polling_interval_minutes: Number(draft.polling_interval_minutes) || 5,
        sync_window_days: Number(draft.sync_window_days) || 30,
        max_threads_per_sync: Number(draft.max_threads_per_sync) || 60,
      };
      const data = await putSettings(patch);
      setPayload(data);
      setDraft(pick(data.settings));
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Could not save settings", { description: errMessage(e) });
    } finally {
      setSaving(false);
    }
  };

  const connect = async () => {
    setBusy("connect");
    try {
      const { auth_url } = await gmailOauthStart();
      window.location.href = auth_url;
    } catch (e) {
      toast.error("Could not start Google sign-in", { description: errMessage(e) });
      setBusy("");
    }
  };

  const runSync = async (forceFull) => {
    setBusy(forceFull ? "resync" : "sync");
    try {
      const res = await gmailSync(forceFull);
      if (res?.skipped) toast("A fetch is already running");
      else toast.success("Gmail fetch complete", { description: res?.summary || "Inbox is up to date." });
      window.dispatchEvent(new CustomEvent("gmail-synced"));
      load();
    } catch (e) {
      toast.error("Sync failed", { description: errMessage(e) });
    } finally {
      setBusy("");
    }
  };

  const disconnect = async () => {
    setBusy("disconnect");
    try {
      const res = await gmailDisconnect(purgeMail);
      toast.success("Gmail disconnected", {
        description: purgeMail ? `${res.purged_threads || 0} stored threads removed.` : "Stored mail was kept.",
      });
      setPurgeMail(false);
      load();
    } catch (e) {
      toast.error("Could not disconnect", { description: errMessage(e) });
    } finally {
      setBusy("");
    }
  };

  const watch = async (start) => {
    setBusy(start ? "watch" : "unwatch");
    try {
      const res = start ? await gmailStartWatch() : await gmailStopWatch();
      toast.success(start ? "Real-time watch started" : "Real-time watch stopped", {
        description: start
          ? `Expires ${res?.expiration ? absTime(res.expiration) : "in ~7 days"} - it is renewed automatically.`
          : "Falling back to scheduled polling.",
      });
      load();
    } catch (e) {
      toast.error(start ? "Could not start the watch" : "Could not stop the watch", { description: errMessage(e) });
    } finally {
      setBusy("");
    }
  };

  const reclassify = async () => {
    if (dirty) {
      toast.error("Save your changes first", {
        description: "The triage uses the saved approver list.",
      });
      return;
    }
    setBusy("reclassify");
    try {
      const res = await reclassifyAll();
      toast.success(`Re-scored ${res.classified} of ${res.threads} thread(s)`, {
        description: (res.failed || []).length
          ? `${res.failed.length} thread(s) could not be re-scored - try again.`
          : "Open the Inbox to see the updated buckets.",
      });
    } catch (e) {
      toast.error("Could not re-run the triage", { description: errMessage(e) });
    } finally {
      setBusy("");
    }
  };

  const loadEvents = async () => {    setBusy("events");
    try {
      const res = await gmailPubsubEvents();
      setEvents(res.items || []);
      if (!(res.items || []).length) toast("No Pub/Sub pushes received yet");
    } catch (e) {
      toast.error("Could not load Pub/Sub events", { description: errMessage(e) });
    } finally {
      setBusy("");
    }
  };

  /* ------------------------------ approvers ------------------------------ */
  const addApprover = () => {
    const email = approverInput.trim().toLowerCase();
    if (!email) return;
    if (!email.includes("@")) {
      toast.error("That does not look like an email address");
      return;
    }
    if ((draft.approver_emails || []).includes(email)) {
      toast("That approver is already on the list");
      setApproverInput("");
      return;
    }
    set("approver_emails", [...(draft.approver_emails || []), email].sort());
    setApproverInput("");
  };

  const removeApprover = (email) =>
    set("approver_emails", (draft.approver_emails || []).filter((a) => a !== email));

  /* --------------------------- required fields --------------------------- */
  const toggleRequired = (key) => {
    const cur = draft.required_fields || [];
    set("required_fields", cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]);
  };

  /* ------------------------------ csv format ----------------------------- */
  const moveColumn = (index, dir) => {
    const fmt = [...(draft.csv_format || [])];
    const to = index + dir;
    if (to < 0 || to >= fmt.length) return;
    [fmt[index], fmt[to]] = [fmt[to], fmt[index]];
    set("csv_format", fmt);
  };

  const patchColumn = (index, patch) => {
    const fmt = [...(draft.csv_format || [])];
    fmt[index] = { ...fmt[index], ...patch };
    set("csv_format", fmt);
  };

  const resetFormat = async () => {
    setBusy("reset-csv");
    try {
      const data = await resetCsvFormat();
      setPayload(data);
      setDraft(pick(data.settings));
      toast.success("CSV format reset to the default Mysa layout");
    } catch (e) {
      toast.error("Could not reset the CSV format", { description: errMessage(e) });
    } finally {
      setBusy("");
    }
  };

  const enabledCount = (draft?.csv_format || []).filter((c) => c.enabled).length;

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success(`${what} copied`);
    } catch (e) {
      toast.error("Could not copy to clipboard", { description: "Select the text and copy it manually." });
    }
  };

  if (loading || !draft) {
    return (
      <div className="space-y-4" data-testid="settings-loading">
        <Skeleton className="h-9 w-64" />
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} className="h-48 w-full rounded-xl" />
        ))}
      </div>
    );
  }

  const connected = !!gmail.connected;
  const columnGroups = groupedColumns();

  return (
    <div className="space-y-5 pb-24" data-testid="settings-page">
      <div className="flex flex-col gap-1">
        <h1 className="flex items-center gap-2 text-lg font-semibold tracking-tight">
          <SettingsIcon className="h-4 w-4 text-primary" aria-hidden="true" />
          Settings
        </h1>
        <p className="text-xs text-muted-foreground">
          Gmail connection, sync cadence, approvers, validation rules and the Mysa CSV layout.
        </p>
      </div>

      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-2">
        {/* Two independent columns: each one packs its own cards, so a short card
            never leaves a hole next to a tall one (the old flat grid aligned rows and
            left ~1200px blank under Gmail/Approvers). Below xl the wrappers collapse
            via `display: contents` and `order-*` keeps the original reading order. */}

        {/* ------------------- Left column: Gmail + Required ------------------- */}
        <div className="contents xl:block xl:space-y-4">
          {/* ------------------------------ Gmail ------------------------------ */}
          <SectionCard
            className="order-1 xl:order-none"
            icon={Mail}
            title="Gmail connection"
            description={`Connect the dedicated service mailbox (${
              gmail.expected_mailbox || "payments.tools@gostops.com"
            }) that is a member of the ${
              gmail.group_address || "payments@gostops.com"
            } group. Never connect the group address itself.`}
            testId="settings-gmail-card"
            action={
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold",
                  connected
                    ? "border-success-border bg-success-surface text-success-text"
                    : gmail.needs_reconnect
                      ? "border-danger-border bg-danger-surface text-danger-text"
                      : "border-warning-border bg-warning-surface text-warning-text"
                )}
                data-testid="settings-gmail-state"
              >
                {connected ? (
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                ) : (
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {connected
                  ? "Connected"
                  : gmail.needs_reconnect
                    ? "Needs reconnect"
                    : gmail.configured
                      ? "Not connected"
                      : "Not configured"}
              </span>
            }
          >
            {!gmail.configured ? (
              <Alert className="border-warning-border bg-warning-surface" data-testid="gmail-setup-alert">
                <AlertTriangle className="h-4 w-4 text-warning-text" aria-hidden="true" />
                <AlertTitle className="text-[13px] text-warning-text">Google credentials missing</AlertTitle>
                <AlertDescription className="text-xs text-warning-text">
                  <span className="block">In Google Cloud console: enable the Gmail API, create a Web OAuth client,
                  add the redirect URI below, then send the admin your{" "}
                  <code className="font-mono">GOOGLE_CLIENT_ID</code> and{" "}
                  <code className="font-mono">GOOGLE_CLIENT_SECRET</code> to add to the backend environment.</span>
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="rounded-lg border border-border bg-secondary p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <Link2 className="h-3 w-3" aria-hidden="true" /> Authorised redirect URI
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => copy(gmail.redirect_uri || "", "Redirect URI")}
                  data-testid="copy-redirect-uri-button"
                >
                  <Copy className="mr-1 h-3 w-3" aria-hidden="true" /> Copy
                </Button>
              </div>
              <p
                className="mt-1 break-all font-mono text-[11px] text-foreground"
                data-testid="gmail-redirect-uri"
              >
                {gmail.redirect_uri || "-"}
              </p>
              <details className="mt-2">
                <summary className="cursor-pointer text-[11px] font-medium text-primary">
                  Scopes requested on the consent screen
                </summary>
                <ul className="mt-1 space-y-0.5" data-testid="gmail-scopes-list">
                  {(gmail.scopes || []).map((s) => (
                    <li key={s} className="break-all font-mono text-[10px] text-muted-foreground">
                      {s}
                    </li>
                  ))}
                </ul>
              </details>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-[11px]">
              <span
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-medium",
                  gmail.token_encrypted
                    ? "border-success-border bg-success-surface text-success-text"
                    : "border-warning-border bg-warning-surface text-warning-text"
                )}
                data-testid="token-encryption-state"
              >
                <ShieldCheck className="h-3 w-3" aria-hidden="true" />
                {gmail.token_encrypted ? "Refresh token encrypted at rest" : "Token encryption unavailable"}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-2.5 py-1 font-medium text-muted-foreground">
                {draft.polling_enabled
                  ? `Polling every ${draft.polling_interval_minutes || 5} min`
                  : "Polling off"}
              </span>
            </div>

            {connected ? (
              <dl className="grid grid-cols-2 gap-3 text-xs" data-testid="gmail-account-details">
                <div>
                  <dt className="text-muted-foreground">Mailbox</dt>
                  <dd className="mt-0.5 break-all font-medium" data-testid="gmail-account-email">
                    {account.email || "-"}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Connected</dt>
                  <dd className="mt-0.5 font-medium">{absTime(account.connected_at) || "-"}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Last sync</dt>
                  <dd className="mt-0.5 font-medium" data-testid="gmail-last-sync">
                    {account.last_sync_at ? `${relTime(account.last_sync_at)}` : "never"}
                  </dd>
                  {account.last_sync_at ? (
                    <dd className="text-[10px] text-muted-foreground" data-testid="gmail-last-sync-abs">
                      {absTime(account.last_sync_at)}
                    </dd>
                  ) : null}
                </div>
                <div>
                  <dt className="text-muted-foreground">Result</dt>
                  <dd className="mt-0.5 font-medium">{account.last_sync_summary || account.last_sync_status || "-"}</dd>
                </div>
              </dl>
            ) : null}

            {gmail.mailbox_mismatch ? (
              <Alert className="border-warning-border bg-warning-surface" data-testid="settings-mailbox-mismatch">
                <AlertTriangle className="h-4 w-4 text-warning-text" aria-hidden="true" />
                <AlertTitle className="text-[13px] text-warning-text">Unexpected mailbox connected</AlertTitle>
                <AlertDescription className="text-xs text-warning-text">
                  This tool expects {gmail.expected_mailbox}. Disconnect and reconnect with the service mailbox so group
                  mail is picked up.
                </AlertDescription>
              </Alert>
            ) : null}

            {account.last_sync_error ? (
              <Alert className="border-danger-border bg-danger-surface" data-testid="gmail-sync-error">
                <AlertTriangle className="h-4 w-4 text-danger-text" aria-hidden="true" />
                <AlertTitle className="text-[13px] text-danger-text">Last sync failed</AlertTitle>
                <AlertDescription className="break-words text-xs text-danger-text">
                  {account.last_sync_error}
                </AlertDescription>
              </Alert>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              {connected ? (
                <>
                  <Button
                    onClick={() => runSync(false)}
                    disabled={!!busy}
                    className="h-9 gap-1.5"
                    data-testid="settings-sync-button"
                  >
                    {busy === "sync" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    Fetch now
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => runSync(true)}
                    disabled={!!busy}
                    className="h-9 gap-1.5 bg-card"
                    data-testid="settings-full-resync-button"
                  >
                    {busy === "resync" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    ) : (
                      <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    Full re-sync
                  </Button>
                  <Button
                    variant="outline"
                    onClick={connect}
                    disabled={!!busy}
                    className="h-9 gap-1.5 bg-card"
                    data-testid="settings-reconnect-button"
                  >
                    {busy === "connect" ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    ) : (
                      <Plug className="h-3.5 w-3.5" aria-hidden="true" />
                    )}
                    Reconnect Gmail
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-9 gap-1.5 border-danger-border bg-card text-danger-text hover:bg-danger-surface"
                        data-testid="settings-disconnect-button"
                      >
                        <Unplug className="h-3.5 w-3.5" aria-hidden="true" /> Disconnect
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-card">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Disconnect {account.email || "this mailbox"}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Syncing stops immediately. Queue rows and exports are untouched.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <label className="flex items-start gap-2 rounded-lg border border-border p-3 text-xs">
                        <Checkbox
                          checked={purgeMail}
                          onCheckedChange={(v) => setPurgeMail(!!v)}
                          data-testid="purge-mail-checkbox"
                        />
                        <span>Also delete the mail copies stored in this app (threads, messages, attachments).</span>
                      </label>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="disconnect-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={disconnect}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          data-testid="disconnect-confirm"
                        >
                          Disconnect
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              ) : (
                <Button
                  onClick={connect}
                  disabled={!gmail.configured || !!busy}
                  className="h-9 gap-1.5"
                  data-testid="settings-connect-gmail-button"
                >
                  {busy === "connect" ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  ) : (
                    <Plug className="h-3.5 w-3.5" aria-hidden="true" />
                  )}
                  {gmail.needs_reconnect ? "Reconnect Gmail" : "Connect Gmail"}
                </Button>
              )}
            </div>
          </SectionCard>

          {/* -------------------------- Required fields ------------------------- */}
          <SectionCard
            className="order-4 xl:order-none"
            icon={AlertTriangle}
            title="Required before export"
            description="A queue row stays in Needs Review until every field selected here is filled in and valid."
            testId="settings-required-card"
            action={
              <span className="rounded-full border border-border bg-secondary px-2.5 py-1 text-[11px] font-semibold" data-testid="required-count">
                {(draft.required_fields || []).length} selected
              </span>
            }
          >
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                placeholder="Filter fields"
                value={fieldQuery}
                onChange={(e) => setFieldQuery(e.target.value)}
                className="h-9 pl-8"
                data-testid="required-field-filter"
              />
            </div>
            <div className="space-y-3 xl:columns-2 xl:gap-x-4 xl:space-y-0">
              {columnGroups.map(({ group, keys }) => {
                const visible = keys.filter((k) =>
                  colLabel(k).toLowerCase().includes(fieldQuery.trim().toLowerCase())
                );
                if (!visible.length) return null;
                return (
                  <div key={group} className="break-inside-avoid xl:mb-3">
                    <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      {group}
                    </p>
                    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2 xl:grid-cols-1">
                      {visible.map((key) => {
                        const checked = (draft.required_fields || []).includes(key);
                        return (
                          <label
                            key={key}
                            className={cn(
                              "flex cursor-pointer items-center gap-2 rounded-lg border p-2 text-xs transition-colors",
                              checked
                                ? "border-primary bg-success-surface"
                                : "border-border bg-card hover:bg-secondary"
                            )}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleRequired(key)}
                              data-testid={`required-${key}`}
                            />
                            <span className="truncate">{colLabel(key)}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        </div>

        {/* ------------------ Right column: Sync + Approvers ------------------- */}
        <div className="contents xl:block xl:space-y-4">
          {/* --------------------------- Sync cadence --------------------------- */}
          <SectionCard
            className="order-2 xl:order-none"
            icon={Clock}
            title="Sync & real-time"
            description="Scheduled polling is the default. Turn on Pub/Sub for instant push once the topic is ready."
            testId="settings-sync-card"
          >
            <div className="rounded-lg border border-border bg-secondary p-3">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Effective Gmail search
              </span>
              <p className="mt-1 break-all font-mono text-[11px]" data-testid="effective-query">
                {payload?.effective_query || "-"}
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="gmail-query" className="text-[13px] font-medium">
                  Gmail query
                </Label>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-[11px]"
                  onClick={() => set("gmail_query", payload?.defaults?.gmail_query || "")}
                  disabled={!payload?.defaults?.gmail_query}
                  data-testid="restore-default-query-button"
                >
                  <RotateCcw className="mr-1 h-3 w-3" aria-hidden="true" /> Default
                </Button>
              </div>
              <Input
                id="gmail-query"
                value={draft.gmail_query ?? ""}
                onChange={(e) => set("gmail_query", e.target.value)}
                className="h-9 font-mono text-xs"
                data-testid="gmail-query-input"
              />
              <p className="text-xs text-muted-foreground">
                Only mail matching this filter is processed - everything else in the mailbox is ignored. By default both
                the group address <code className="font-mono">{gmail.group_address || "payments@gostops.com"}</code> and
                mail sent straight to{" "}
                <code className="font-mono">{gmail.expected_mailbox || "payments.tools@gostops.com"}</code> are included
                (to / cc / bcc). The sync window is appended automatically as{" "}
                <code className="font-mono">newer_than:Nd</code>. If something still is not matching, try adding{" "}
                <code className="font-mono">OR deliveredto:{gmail.expected_mailbox || "payments.tools@gostops.com"}</code>
                .
              </p>
            </div>

            <ToggleRow
              id="polling-enabled"
              label="Scheduled polling"
              hint="Background poller checks Gmail on the interval below."
              checked={draft.polling_enabled}
              onCheckedChange={(v) => set("polling_enabled", v)}
              testId="polling-enabled-switch"
            />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <NumberRow
                id="polling-interval"
                label="Polling interval"
                value={draft.polling_interval_minutes}
                onChange={(v) => set("polling_interval_minutes", v)}
                min={1}
                max={1440}
                suffix="minutes"
                fallback={settings.polling_interval_minutes || 5}
                testId="polling-interval-input"
              />
              <NumberRow
                id="sync-window"
                label="Sync window"
                value={draft.sync_window_days}
                onChange={(v) => set("sync_window_days", v)}
                min={1}
                max={3650}
                suffix="days back"
                fallback={settings.sync_window_days || 30}
                testId="sync-window-input"
              />
              <NumberRow
                id="max-threads"
                label="Max threads per sync"
                value={draft.max_threads_per_sync}
                onChange={(v) => set("max_threads_per_sync", v)}
                min={1}
                max={500}
                suffix="threads"
                fallback={settings.max_threads_per_sync || 60}
                testId="max-threads-input"
              />
              <ToggleRow
                id="auto-classify"
                label="Auto-classify with Claude"
                hint="Categorise every new thread on arrival."
                checked={draft.auto_classify}
                onCheckedChange={(v) => set("auto_classify", v)}
                testId="auto-classify-switch"
              />
            </div>

            <ToggleRow
              id="auto-extract-approvals"
              label="Auto-extract approved mail to the queue"
              hint="When a mail is classified “Approval Replies” and carries an approved amount, vendor rows are extracted in the background - no Extract click needed."
              checked={draft.auto_extract_approvals}
              onCheckedChange={(v) => set("auto_extract_approvals", v)}
              testId="auto-extract-approvals-switch"
            />

            <Separator />

            <ToggleRow
              id="pubsub-enabled"
              label="Real-time push (Google Pub/Sub)"
              hint="Requires a Pub/Sub topic with a push subscription pointing at /api/gmail/pubsub/push."
              checked={draft.pubsub_enabled}
              onCheckedChange={(v) => set("pubsub_enabled", v)}
              testId="pubsub-enabled-switch"
            />

            {draft.pubsub_enabled ? (
              <div className="space-y-3 rounded-lg border border-border p-3" data-testid="pubsub-panel">
                <div className="rounded-lg border border-border bg-secondary p-2.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                      Push endpoint for the subscription
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-[11px]"
                      onClick={() => copy(gmail.pubsub_push_endpoint || "", "Push endpoint")}
                      data-testid="copy-push-endpoint-button"
                    >
                      <Copy className="mr-1 h-3 w-3" aria-hidden="true" /> Copy
                    </Button>
                  </div>
                  <p className="mt-1 break-all font-mono text-[11px]" data-testid="pubsub-push-endpoint">
                    {gmail.pubsub_push_endpoint || "-"}
                  </p>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Grant <code className="font-mono">gmail-api-push@system.gserviceaccount.com</code> the Pub/Sub
                    Publisher role on the topic, then point a push subscription at the URL above.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pubsub-topic" className="text-[13px] font-medium">
                    Pub/Sub topic
                  </Label>
                  <Input
                    id="pubsub-topic"
                    placeholder="projects/my-project/topics/gmail-payments"
                    value={draft.pubsub_topic ?? ""}
                    onChange={(e) => set("pubsub_topic", e.target.value)}
                    className="h-9 font-mono text-xs"
                    data-testid="pubsub-topic-input"
                  />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="outline"
                    className="h-8 gap-1.5 bg-card text-xs"
                    onClick={() => watch(true)}
                    disabled={!connected || !!busy}
                    data-testid="start-watch-button"
                  >
                    {busy === "watch" ? (
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                    ) : (
                      <RadioTower className="h-3 w-3" aria-hidden="true" />
                    )}
                    Start watch
                  </Button>
                  <Button
                    variant="outline"
                    className="h-8 gap-1.5 bg-card text-xs"
                    onClick={() => watch(false)}
                    disabled={!connected || !!busy}
                    data-testid="stop-watch-button"
                  >
                    Stop watch
                  </Button>
                  <Button
                    variant="ghost"
                    className="h-8 gap-1.5 text-xs"
                    onClick={loadEvents}
                    disabled={!!busy}
                    data-testid="load-pubsub-events-button"
                  >
                    {busy === "events" ? (
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                    ) : (
                      <Search className="h-3 w-3" aria-hidden="true" />
                    )}
                    Recent pushes
                  </Button>
                </div>
                {account.watch_expiration ? (
                  <p className="text-xs text-muted-foreground" data-testid="watch-expiry">
                    Watch active until {absTime(account.watch_expiration)}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No active watch - polling keeps running as the fallback.
                  </p>
                )}
                {events.length ? (
                  <ul className="space-y-1" data-testid="pubsub-events-list">
                    {events.slice(0, 5).map((ev, i) => (
                      <li key={`${ev.history_id}-${i}`} className="font-mono text-[11px] text-muted-foreground">
                        {relTime(ev.received_at) || "just now"} · history {ev.history_id || "-"}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            ) : null}
          </SectionCard>

          {/* ---------------------------- Approvers ---------------------------- */}
          <SectionCard
            className="order-3 xl:order-none"
            icon={CheckCircle2}
            title="Approvers"
            description="Approval replies from these addresses are trusted when the AI links an approval to a vendor request. After changing this list, re-run the triage so existing mails are re-scored."
            testId="settings-approvers-card"
            action={
              <Button
                variant="outline"
                size="sm"
                className="h-8 gap-1.5 bg-card text-xs"
                onClick={reclassify}
                disabled={!!busy}
                data-testid="reclassify-all-button"
              >
                {busy === "reclassify" ? (
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                ) : (
                  <Sparkles className="h-3 w-3" aria-hidden="true" />
                )}
                Re-run triage
              </Button>
            }
          >
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                placeholder="approver@gostops.com"
                value={approverInput}
                onChange={(e) => setApproverInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addApprover();
                  }
                }}
                className="h-9"
                data-testid="approver-input"
              />
              <Button onClick={addApprover} className="h-9 gap-1.5 sm:w-auto" data-testid="add-approver-button">
                <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add
              </Button>
            </div>
            {(draft.approver_emails || []).length ? (
              <ul className="flex flex-wrap gap-2" data-testid="approver-list">
                {(draft.approver_emails || []).map((email) => (
                  <li
                    key={email}
                    className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary px-3 py-1 text-xs"
                    data-testid={`approver-chip-${email}`}
                  >
                    <span className="max-w-[220px] truncate">{email}</span>
                    <button
                      type="button"
                      onClick={() => removeApprover(email)}
                      className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-card hover:text-danger-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={`Remove ${email}`}
                      data-testid={`remove-approver-${email}`}
                    >
                      <X className="h-3 w-3" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="rounded-lg border border-dashed border-border p-3 text-xs text-muted-foreground" data-testid="approvers-empty">
                No approvers yet. Add the finance approvers who reply on the payment thread.
              </p>
            )}
          </SectionCard>
        </div>
      </div>

      {/* ------------------------------ CSV format ------------------------------ */}
      <SectionCard
        icon={Tag}
        title="Mysa CSV format"
        description="Order, rename and switch off columns. This exact layout is what the Export screen produces."
        testId="settings-csv-card"
        action={
          <div className="flex items-center gap-2">
            <span
              className="rounded-full border border-border bg-secondary px-2.5 py-1 text-[11px] font-semibold"
              data-testid="csv-enabled-count"
            >
              {enabledCount} of {(draft.csv_format || []).length} columns
            </span>
            <Button
              variant="outline"
              className="h-8 gap-1.5 bg-card text-xs"
              onClick={resetFormat}
              disabled={!!busy}
              data-testid="reset-csv-format-button"
            >
              {busy === "reset-csv" ? (
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              ) : (
                <RotateCcw className="h-3 w-3" aria-hidden="true" />
              )}
              Reset
            </Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="csv-prefix" className="text-[13px] font-medium">
              File name prefix
            </Label>
            <Input
              id="csv-prefix"
              value={draft.csv_filename_prefix ?? ""}
              onChange={(e) => set("csv_filename_prefix", e.target.value)}
              className="h-9 font-mono text-xs"
              data-testid="csv-prefix-input"
            />
            <p className="text-xs text-muted-foreground">
              Exports are saved as{" "}
              <code className="font-mono">{(draft.csv_filename_prefix || "mysa_vendors")}_YYYY-MM-DD_xxxxxx.csv</code>
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="label-name" className="text-[13px] font-medium">
              Gmail label applied after export
            </Label>
            <Input
              id="label-name"
              value={draft.label_name ?? ""}
              onChange={(e) => set("label_name", e.target.value)}
              className="h-9 font-mono text-xs"
              data-testid="label-name-input"
            />
            <ToggleRow
              id="auto-label"
              label="Label threads automatically on export"
              checked={draft.auto_label}
              onCheckedChange={(v) => set("auto_label", v)}
              testId="auto-label-switch"
            />
          </div>
        </div>

        <div
          className="overflow-hidden rounded-xl border border-border"
          data-testid="csv-format-editor"
        >
          <div className="hidden bg-secondary px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground sm:grid sm:grid-cols-[48px_1fr_1fr_88px] sm:gap-3">
            <span>On</span>
            <span>Source field</span>
            <span>CSV header</span>
            <span className="text-right">Order</span>
          </div>
          <ul className="divide-y divide-border">
            {(draft.csv_format || []).map((col, index) => (
              <li
                key={col.key}
                className={cn(
                  "grid grid-cols-[40px_1fr] items-center gap-3 px-3 py-2.5 sm:grid-cols-[48px_1fr_1fr_88px]",
                  col.enabled ? "bg-card" : "bg-secondary"
                )}
                data-testid={`csv-col-${col.key}`}
              >
                <Switch
                  checked={!!col.enabled}
                  onCheckedChange={(v) => patchColumn(index, { enabled: !!v })}
                  aria-label={`Include ${colLabel(col.key)}`}
                  data-testid={`csv-col-toggle-${col.key}`}
                />
                <div className="min-w-0">
                  <p className="truncate text-[13px] font-medium">{colLabel(col.key)}</p>
                  <p className="truncate font-mono text-[10px] text-muted-foreground">{col.key}</p>
                </div>
                <Input
                  value={col.header ?? ""}
                  onChange={(e) => patchColumn(index, { header: e.target.value })}
                  className="col-span-2 h-8 text-xs sm:col-span-1"
                  aria-label={`CSV header for ${colLabel(col.key)}`}
                  data-testid={`csv-col-header-${col.key}`}
                />
                <div className="col-span-2 flex items-center justify-end gap-1 sm:col-span-1">
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 bg-card"
                    onClick={() => moveColumn(index, -1)}
                    disabled={index === 0}
                    aria-label={`Move ${colLabel(col.key)} up`}
                    data-testid={`csv-col-up-${col.key}`}
                  >
                    <ArrowUp className="h-3 w-3" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-7 w-7 bg-card"
                    onClick={() => moveColumn(index, 1)}
                    disabled={index === (draft.csv_format || []).length - 1}
                    aria-label={`Move ${colLabel(col.key)} down`}
                    data-testid={`csv-col-down-${col.key}`}
                  >
                    <ArrowDown className="h-3 w-3" aria-hidden="true" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-xs text-muted-foreground">
          Need to see the result?{" "}
          <Link to="/export" className="font-medium text-primary underline-offset-2 hover:underline" data-testid="settings-to-export-link">
            Open the Export screen
          </Link>{" "}
          for a live preview.
        </p>
      </SectionCard>

      {/* --------------------------- Users & access --------------------------- */}
      <UsersSection />

      {/* ------------------------------ Save bar ------------------------------ */}
      <div
        className={cn(
          "fixed inset-x-0 bottom-0 z-30 border-t border-border bg-card px-4 py-3 transition-opacity duration-200 sm:px-6 lg:px-8",
          dirty ? "opacity-100" : "pointer-events-none opacity-0"
        )}
        data-testid="settings-save-bar"
      >
        <div className="mx-auto flex max-w-[1680px] items-center justify-between gap-3">
          <p className="text-xs text-muted-foreground">You have unsaved changes.</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="h-9 bg-card text-xs"
              onClick={() => setDraft(pick(settings))}
              disabled={saving}
              data-testid="settings-discard-button"
            >
              Discard
            </Button>
            <Button onClick={save} disabled={saving} className="h-9 gap-1.5" data-testid="settings-save-button">
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Save className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              Save changes
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
